import React from 'react';
import {Link} from 'react-router';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {isArray} from 'lodash';
import {Checkbox, Icon, ListGroupItem, Text, IconButton, WithTooltip} from '@albert-io/atomic';
import {getQuestionEditPath} from 'client/Dennis/Content/Queue/shared';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import './question.scss';

import PartialCreditIcon from 'client/components/PartialCreditIcon/PartialCreditIcon';

import ArchivedQuestionBadge from 'client/components/ArchivedQuestions/ArchivedQuestionBadge.react';
import {RestrictionIndicator} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {
  TeacherRestrictionIndicator,
  isQuestionRestrictedForTeacher
} from 'client/components/TeacherRestrictedContent';
import {RESTRICTED_QUESTION_LABEL} from 'client/components/TeacherRestrictedContent/teacherRestriction.utils';
import CONSTANTS from 'client/constants';

const boolOrFunctionType = PropTypes.oneOfType([PropTypes.bool, PropTypes.func]);
const stringOrFunctionType = PropTypes.oneOfType([PropTypes.string, PropTypes.func]);

function withActionEventHandler(callback = () => {}) {
  return (e) => {
    e.preventDefault();
    e.stopPropagation();
    callback(e);
  };
}

export const sharedQuestionPropTypes = {
  showDifficulty: boolOrFunctionType,
  showAttemptIndicators: boolOrFunctionType,
  isCorrect: boolOrFunctionType,
  isIncorrect: boolOrFunctionType,
  isBookmarked: boolOrFunctionType,
  onQuestionClick: PropTypes.func,
  restrictionGuideLevelName: stringOrFunctionType,
  restrictionGuideLevelId: stringOrFunctionType,
  restrictionGuideLevelHref: stringOrFunctionType,
  restrictionGuideLevelIds: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.func
  ])
};

const ClickableIndicator = ({active, isRearrangeElement}) => (
  <div
    className={classnames('clickable-indicator', {
      'clickable-indicator--active': active && !isRearrangeElement
    })}
  />
);

ClickableIndicator.propTypes = {
  active: PropTypes.bool,
  isRearrangeElement: PropTypes.bool
};

const attemptIndicatorResultValues = {
  correct: ['positive', 'check'],
  incorrect: ['negative', 'times']
};

const AttemptIndicator = ({result}) => {
  if (!result) {
    return null;
  }

  const color = result === 'incorrect' ? 'negative' : 'positive';
  const icon = result === 'incorrect' ? 'times' : 'check';

  return result === 'partiallyCorrect' ? (
    <div className='ml-x2 w-x4 text-content-positive text-center'>
      <PartialCreditIcon />
    </div>
  ) : (
    <Icon icon={icon} color={color} className='question-v2-attempt-indicator' />
  );
};

AttemptIndicator.propTypes = {
  result: PropTypes.oneOf(Object.keys(attemptIndicatorResultValues))
};

export default class Question extends React.Component {
  static Checkbox = ({onChange, checked, idx, disabled, disabledTooltip}) => {
    const checkbox = (
      <Checkbox
        className='question-v2-checkbox'
        onClick={withActionEventHandler()}
        onChange={onChange}
        checked={disabled ? false : checked}
        data-testid={`question-checkbox-${idx}`}
        disabled={disabled}
        size='s'
      />
    );
    return disabledTooltip ? (
      <WithTooltip
        enabled={!!disabled}
        content={disabledTooltip}
        display='flex'
        // The tooltip wrapper sits inside the row Link: swallow clicks so the
        // disabled checkbox's dead zone doesn't trigger navigation.
        wrapperProps={{onClick: withActionEventHandler()}}
      >
        {checkbox}
      </WithTooltip>
    ) : (
      checkbox
    );
  };

  static DuplicateSetIcon = ({question, questionSet}) => (
    <IconButton
      as={Link}
      disabled={!question}
      to={
        question &&
        getQuestionEditPath({
          questionSetId: questionSet.getId(),
          questionId: question.getId(),
          customSearch: {
            status: 'published',
            subject: questionSet.getAuthoringSubject().getId(),
            page: questionSet.getId(),
            fallback_page: 1
          }
        })
      }
      className='mr-x1'
      icon={['far', 'exclamation-triangle']}
      tooltip='One or more questions in this set have duplicate alignment.'
      tooltipProps={{enabled: !question}}
    />
  );

  static propTypes = {
    ...sharedQuestionPropTypes,
    as: PropTypes.string,
    clickable: PropTypes.bool,
    questionPath: PropTypes.string,
    isNonMatchingSearchResult: PropTypes.bool
  };

  static defaultProps = {
    as: 'li',
    clickable: true,
    showDifficulty: true,
    showAttemptIndicators: true,
    onQuestionClick: () => {}
  };

  keyElement = (element, i) => {
    return React.cloneElement(element, {key: i});
  };

  getActions(actions) {
    if (!actions) {
      return null;
    }
    return isArray(actions) ? actions.map(this.keyElement) : actions;
  }

  handleQuestionClick = (e) => {
    if (this.props.clickable) {
      this.props.onQuestionClick(e);
    }
  };

  render() {
    const {
      actions,
      as,
      attemptIndicatorValue,
      difficulty,
      hasAccess,
      heading,
      isActive,
      isBookmarked,
      showAttemptIndicators,
      showDifficulty,
      strikethroughQuestionInfo,
      title,
      questionPath,
      isNonMatchingSearchResult,
      isSelectable,
      isRearrangeable,
      clickable,
      dragHandle,
      questionSetPosition,
      restrictionGuideLevelName,
      restrictionGuideLevelHref,
      restrictionGuideLevelId,
      restrictionGuideLevelIds
    } = this.props;

    const isArchivedQuestion =
      this.props.question?.getStatus() === CONSTANTS.QUESTIONSET_STATUSES.ARCHIVED;
    const isRestrictedForTeacher = isQuestionRestrictedForTeacher(this.props.question);

    const WrapperEl = questionPath ? Link : 'div';
    return (
      <ListGroupItem
        as={as}
        className={classnames('question-v2', {
          'question-v2--non-matching-result': isNonMatchingSearchResult,
          'question-v2--selectable': isSelectable,
          'question-v2--clickable': clickable,
          'question-v2--rearrangeable': isRearrangeable
        })}
        paddingLevel={0}
        data-testid={`question-${questionSetPosition}`}
      >
        <WrapperEl
          className='question-v2__wrapper'
          data-testid={`question-wrapper-${questionSetPosition}`}
          onClick={this.handleQuestionClick}
          to={questionPath}
        >
          {isRearrangeable ? (
            dragHandle
          ) : (
            <div
              className='question-v2__actions'
              data-testid={`question-actions-${questionSetPosition}`}
            >
              {this.getActions(actions)}
            </div>
          )}
          <div
            className={classnames('question-v2__meta', {
              'question-v2__meta--strikethrough': strikethroughQuestionInfo
            })}
          >
            <Text
              as={MarkdownRendererV2}
              size='m'
              bold={isRestrictedForTeacher ? false : heading}
              className='question-v2__title'
              text={isRestrictedForTeacher ? RESTRICTED_QUESTION_LABEL : title}
              color={isRestrictedForTeacher || isNonMatchingSearchResult ? 'secondary' : 'primary'}
            />
            <div className='flex flex-col'>
              {difficulty && showDifficulty && (
                <Text size='xs' color='secondary' className='question-v2__difficulty'>
                  {isNonMatchingSearchResult
                    ? 'This question did not match your search'
                    : difficulty}
                </Text>
              )}
              {isArchivedQuestion && <ArchivedQuestionBadge classname='mt-x1' />}
            </div>
          </div>
          <div className='question-v2__info'>
            {/* Gate on id, not name: folder surfaces pass the title as name, so an empty name must not hide a genuine restriction. */}
            {restrictionGuideLevelId && (
              <span
                className='mr-x1 inline-flex'
                role='presentation'
                onClick={(e) => {
                  // Lock sits inside the row Link: stop the anchor nav and the row's click handler.
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <RestrictionIndicator
                  guideLevelName={restrictionGuideLevelName}
                  guideLevelId={restrictionGuideLevelId}
                  ancestorGuideLevelIds={restrictionGuideLevelIds}
                  assessmentGuideHref={restrictionGuideLevelHref}
                  variant='question'
                  placement='right-start'
                  usePortal
                />
              </span>
            )}
            {/* Mutually exclusive with the admin indicator above, which never shows for teachers. */}
            {isRestrictedForTeacher && (
              <span
                className='mr-x1 inline-flex'
                role='presentation'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <TeacherRestrictionIndicator placement='right-start' />
              </span>
            )}
            {showAttemptIndicators && <AttemptIndicator result={attemptIndicatorValue} />}
            {!hasAccess && (
              <Icon icon='lock' color='tertiary' className='question-v2-locked-indicator' />
            )}
            {isBookmarked && (
              // eslint-disable-next-line local-rules/use-tailwind-semantic-classes
              <Icon icon='bookmark' className='question-v2-saved-info-el text-purple-200' />
            )}
          </div>
        </WrapperEl>
        {clickable && (
          <ClickableIndicator
            active={isActive}
            isRearrangeElement={this.props.isActiveRearrangeElement}
          />
        )}
      </ListGroupItem>
    );
  }
}
